import React, { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import Badge from '../ui/Badge';
import KbdShortcut from '../ui/KbdShortcut';
import CommandPalette from './CommandPalette';

const PAGE_META = {
  data:   { title: 'Dashboard', subtitle: 'Stops and routes overview' },
  stops:  { title: 'Stops',     subtitle: 'Add individual stops or import them in bulk' },
  map:    { title: 'Map',       subtitle: 'Visualize current selection' },
  routes: { title: 'Routes',    subtitle: 'Plan, edit, and review routes' }
};

export default function Topbar({ controller }) {
  const { activeTab, savedRoutes, stops } = controller;
  const meta = PAGE_META[activeTab] || PAGE_META.data;
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Global ⌘K / Ctrl+K opens quick search.
  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <header className="sticky top-0 z-20 h-14 bg-white/85 backdrop-blur border-b border-slate-200 flex items-center px-4 lg:px-6 gap-4">
      <div className="flex items-baseline gap-3 min-w-0">
        <h1 className="text-[15px] font-semibold text-slate-900 leading-none truncate">{meta.title}</h1>
        <span className="hidden md:inline text-[12px] text-slate-500 truncate">{meta.subtitle}</span>
      </div>

      <div className="ml-auto flex items-center gap-2">
        <div className="hidden md:flex items-center gap-1.5">
          <Badge variant="outline" size="md" dot>
            <span className="tabular-nums">{stops.length}</span>
            <span className="text-slate-500 font-normal ml-1">stops</span>
          </Badge>
          <Badge variant="brand" size="md" dot>
            <span className="tabular-nums">{savedRoutes.length}</span>
            <span className="text-brand-600/80 font-normal ml-1">routes</span>
          </Badge>
        </div>

        <button
          type="button"
          onClick={() => setPaletteOpen(true)}
          className="hidden lg:inline-flex items-center gap-2 h-9 pl-3 pr-2 rounded-md border border-slate-200 bg-white text-[13px] text-slate-400 hover:border-slate-300 hover:text-slate-600 transition-colors w-[220px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
        >
          <Search className="h-3.5 w-3.5" />
          <span className="flex-1 text-left">Quick search…</span>
          <span className="flex items-center gap-0.5">
            <KbdShortcut>⌘</KbdShortcut>
            <KbdShortcut>K</KbdShortcut>
          </span>
        </button>

        {/* Compact search trigger on smaller screens */}
        <button
          type="button"
          onClick={() => setPaletteOpen(true)}
          aria-label="Quick search"
          className="lg:hidden inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
        >
          <Search className="h-4 w-4" />
        </button>
      </div>

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        controller={controller}
      />
    </header>
  );
}
